import { Stack } from '@mui/joy';
import { ICellRendererParams } from 'ag-grid-community';
import { t } from 'i18next';
import { upperFirst } from 'lodash';
import { useEffect, useState } from 'react';

import { StandardCost } from 'bom/elements';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { Margin, Price } from 'core/elements';
import { getAgGridTableCellData } from 'core/utils';
import { PricingHelper } from 'pricing/helpers';
import { QuotePartColId } from 'quotes/components/QuotePartList';
import { QuoteType } from 'quotes/enums';
import { IQuotePart } from 'quotes/types';
import { isBaseAdderPnbPricingEnabled, isCostPnbFixed } from 'quotes/utils';
import { Direction } from 'theme/enums';

interface IProps extends ICellRendererParams {
  type: string;
  onMarginClick?: (quotePart: IQuotePart) => void;
  isRowEditable?: ({ data }) => boolean;
  quoteType?: QuoteType;
  costDecimals?: number;
}

const PnbPricingCell = ({
  type = 'base',
  onMarginClick,
  isRowEditable,
  column,
  quoteType = QuoteType.CUSTOMER,
  costDecimals = PricingHelper.DEFAULT_COST_DECIMALS,
  ...rest
}: IProps) => {
  const [columnWidth, setColumnWidth] = useState(column.getActualWidth());

  useEffect(() => {
    const handleResize = () => {
      setColumnWidth(column.getActualWidth());
    };

    column.addEventListener('widthChanged', handleResize);

    return () => {
      column.removeEventListener('widthChanged', handleResize);
    };
  }, [column]);

  const data = getAgGridTableCellData(rest, QuotePartColId.COST);

  if (!data) {
    return rest?.value;
  }

  const editableMargin = isRowEditable ? isRowEditable({ data }) : false;

  const getPricingValues = () => {
    const propType = upperFirst(type);

    return {
      computedCost: data[`computedCost${propType}`],
      computedPurchaseCost: data[`computedPurchaseCost${propType}`],
      price: data[`price${propType}`],
      quotePriceDecimals: data.quotePriceDecimals,
    };
  };

  const { computedCost, computedPurchaseCost, price, quotePriceDecimals } = getPricingValues();

  if (QuoteType.VENDOR === quoteType) {
    return <Price amount={price} decimals={costDecimals} label={`${t('labelCost')}:`} />;
  }

  if (!isBaseAdderPnbPricingEnabled(data) || isCostPnbFixed(data)) {
    return DEFAULT_PLACEHOLDER;
  }

  return (
    <Stack key={columnWidth} direction={columnWidth > 220 ? Direction.ROW : Direction.COLUMN}>
      <Stack>
        <StandardCost
          cost={computedCost}
          decimals={costDecimals}
          fixedDecimals
          label={`${t('labelStandardCost')}:`}
        />

        <Price
          amount={computedPurchaseCost}
          decimals={costDecimals}
          fixedDecimals
          label={`${t('labelPurchaseCost')}:`}
        />
      </Stack>

      <Stack>
        <Price amount={price} decimals={quotePriceDecimals} label={`${t('labelPrice')}:`} />

        <Margin
          price={price}
          computedCost={computedCost}
          onClick={editableMargin ? () => onMarginClick(data) : undefined}
          editable={editableMargin}
        />
      </Stack>
    </Stack>
  );
};

export default PnbPricingCell;
